import {
  GPU_QUEUE_SUBMIT_HISTORY_CAPACITY,
  getGPUQueueSubmitObserver,
  runGPUQueueSubmitObserverSelfTest,
  type GPUQueueSubmitObservation,
  type GPUQueueSubmitTarget,
} from './gpuQueueSubmitObserver';

export type PhysicsSubmissionClass = 'critical' | 'topology' | 'diagnostic' | 'init';

export type PhysicsSubmissionRecord = {
  sequence: number;
  frameId: number;
  submissionClass: PhysicsSubmissionClass;
  label: string;
  count: number;
  fixedStepId: number | null;
  fixedStepSpan: number;
  substepIndex: number | null;
};

export type PhysicsSubmissionCounts = Record<PhysicsSubmissionClass, number>;

export type PhysicsActualSubmissionRecord = {
  sequence: number;
  /** Successful-submit ordinal reported by the observer shared by this queue. */
  queueSequence: number;
  frameId: number;
  submissionClass: PhysicsSubmissionClass | null;
  label: string | null;
  commandBufferCount: number | null;
  scopeDepth: number;
  attributed: boolean;
  fixedStepId: number | null;
  fixedStepSpan: number;
  substepIndex: number | null;
};

export type PhysicsActualSubmissionCounts = {
  submitCalls: number;
  /** Sum for submits whose command-buffer argument was an Array. */
  knownCommandBuffers: number;
  unknownCommandBufferCountSubmits: number;
  attributedSubmits: number;
  unattributedSubmits: number;
  byClass: PhysicsSubmissionCounts;
};

export type PhysicsActualSubmitMismatch = {
  /** False means actual-vs-intent comparison is unavailable, not a match. */
  measured: boolean;
  intentSubmits: number;
  actualSubmits: number;
  /** actualSubmits - intentSubmits, or null when observation is unavailable. */
  delta: number | null;
  matches: boolean | null;
  /** Actual minus intent for attributed submissions in each class. */
  byClassDelta: Record<PhysicsSubmissionClass, number | null>;
};

export type PhysicsSubmissionSnapshot = {
  frameId: number;
  fixedSteps: number;
  substepsPerFixedStep: number;
  advancedSimulation: boolean;
  counts: PhysicsSubmissionCounts;
  /** Physics-owned submit intents excluding asynchronous diagnostic readbacks. */
  coreSubmitIntents: number;
  /** Automatic diagnostic readback submits, including renderer requests and raw copy submits. */
  diagnosticReadbackIntents: number;
  /** All explicitly owned queue/renderer submit intents in this frame. */
  totalOwnedSubmitIntents: number;
  criticalSubmits: number;
  steadyCriticalBudget: number;
  withinSteadyCriticalBudget: boolean;
  records: PhysicsSubmissionRecord[];
  cumulative: PhysicsSubmissionCounts;
  peakCriticalSubmits: number;
  peakOwnedSubmitIntents: number;
  actualSubmitObservationAttempted: boolean;
  /** True only when at least one scope was attempted and every hook succeeded. */
  actualSubmitObservationAvailable: boolean;
  actualSubmitObservationFailureReason: string | null;
  actualRecords: PhysicsActualSubmissionRecord[];
  actualCounts: PhysicsActualSubmissionCounts;
  unattributedActualSubmits: number;
  actualIntentMismatch: PhysicsActualSubmitMismatch;
};

type RecordOptions = {
  frameId?: number;
  count?: number;
  fixedStepId?: number | null;
  fixedStepSpan?: number;
  substepIndex?: number | null;
};

export type PhysicsActualSubmitScopeOptions = Omit<RecordOptions, 'count'> & {
  /** Omit class and label for a frame-wide catch-all scope. */
  submissionClass?: PhysicsSubmissionClass;
  label?: string;
};

type FrameBucket = {
  fixedSteps: number;
  substepsPerFixedStep: number;
  advancedSimulation: boolean;
  records: PhysicsSubmissionRecord[];
  actualRecords: PhysicsActualSubmissionRecord[];
  actualObservationAttempted: boolean;
  actualObservationAvailable: boolean;
  actualObservationFailureReason: string | null;
};

type QueueActualClaimState = {
  sequences: Set<number>;
  retainedStartCursor: number;
};

const SUBMISSION_CLASSES: readonly PhysicsSubmissionClass[] = [
  'critical',
  'topology',
  'diagnostic',
  'init',
];

function emptyCounts(): PhysicsSubmissionCounts {
  return { critical: 0, topology: 0, diagnostic: 0, init: 0 };
}

function emptyActualCounts(): PhysicsActualSubmissionCounts {
  return {
    submitCalls: 0,
    knownCommandBuffers: 0,
    unknownCommandBufferCountSubmits: 0,
    attributedSubmits: 0,
    unattributedSubmits: 0,
    byClass: emptyCounts(),
  };
}

function emptyFrameBucket(): FrameBucket {
  return {
    fixedSteps: 0,
    substepsPerFixedStep: 0,
    advancedSimulation: false,
    records: [],
    actualRecords: [],
    actualObservationAttempted: false,
    actualObservationAvailable: false,
    actualObservationFailureReason: null,
  };
}

/**
 * Explicit intent accounting plus opt-in observation of real queue submits.
 * Actual observation is restricted to synchronous scopes, which keeps
 * unrelated renderer/browser work out and allows shared queues without one
 * PhysicsEngine counting another engine's submissions.
 */
export class PhysicsSubmissionLedger {
  private readonly frames = new Map<number, FrameBucket>();
  private readonly cumulativeCounts = emptyCounts();
  private currentFrameId = -1;
  private sequence = 0;
  private actualSequence = 0;
  private peakCritical = 0;
  private peakOwned = 0;
  private readonly historyLimit: number;
  private actualClaimsByQueue = new WeakMap<object, QueueActualClaimState>();

  constructor(historyLimit = 240) {
    this.historyLimit = Math.max(8, Math.floor(historyLimit));
  }

  beginFrame(frameId: number): void {
    const boundedFrame = Math.max(0, Math.floor(frameId));
    this.currentFrameId = boundedFrame;
    if (!this.frames.has(boundedFrame)) {
      this.frames.set(boundedFrame, emptyFrameBucket());
    }
    this.pruneHistory();
  }

  setFrameWorkload(
    frameId: number,
    fixedSteps: number,
    substepsPerFixedStep: number,
    advancedSimulation: boolean,
  ): void {
    const bucket = this.ensureFrame(frameId);
    bucket.fixedSteps = Math.max(0, Math.floor(fixedSteps));
    bucket.substepsPerFixedStep = Math.max(0, Math.floor(substepsPerFixedStep));
    bucket.advancedSimulation = advancedSimulation;
  }

  record(
    submissionClass: PhysicsSubmissionClass,
    label: string,
    options: RecordOptions = {},
  ): void {
    const frameId = Math.max(
      0,
      Math.floor(options.frameId ?? Math.max(0, this.currentFrameId)),
    );
    const count = Math.max(1, Math.floor(options.count ?? 1));
    const bucket = this.ensureFrame(frameId);
    const record: PhysicsSubmissionRecord = {
      sequence: this.sequence++,
      frameId,
      submissionClass,
      label,
      count,
      fixedStepId: options.fixedStepId == null
        ? null
        : Math.max(0, Math.floor(options.fixedStepId)),
      fixedStepSpan: Math.max(1, Math.floor(options.fixedStepSpan ?? 1)),
      substepIndex: options.substepIndex == null
        ? null
        : Math.max(0, Math.floor(options.substepIndex)),
    };
    bucket.records.push(record);
    this.cumulativeCounts[submissionClass] += count;
    const owned = bucket.records.reduce((sum, entry) => sum + entry.count, 0);
    this.peakOwned = Math.max(this.peakOwned, owned);
    if (submissionClass === 'critical') {
      const critical = bucket.records.reduce(
        (sum, entry) => sum + (entry.submissionClass === 'critical' ? entry.count : 0),
        0,
      );
      this.peakCritical = Math.max(this.peakCritical, critical);
    }
  }

  withActualSubmitScope<T>(queue: GPUQueueSubmitTarget, callback: () => T): T;
  withActualSubmitScope<T>(
    queue: GPUQueueSubmitTarget,
    options: PhysicsActualSubmitScopeOptions,
    callback: () => T,
  ): T;
  withActualSubmitScope<T>(
    queue: GPUQueueSubmitTarget,
    optionsOrCallback: PhysicsActualSubmitScopeOptions | (() => T),
    maybeCallback?: () => T,
  ): T {
    const options = typeof optionsOrCallback === 'function' ? {} : optionsOrCallback;
    const callback = typeof optionsOrCallback === 'function' ? optionsOrCallback : maybeCallback;
    if (!callback) throw new TypeError('withActualSubmitScope requires a synchronous callback.');

    const frameId = Math.max(
      0,
      Math.floor(options.frameId ?? Math.max(0, this.currentFrameId)),
    );
    const bucket = this.ensureFrame(frameId);
    const observer = getGPUQueueSubmitObserver(queue);
    const availableAtEntry = observer.available;
    const entryCursor = observer.cursor;
    const scopeDepth = observer.scopeDepth + 1;
    const listenerFailuresAtEntry = observer.listenerFailureCount;
    this.updateActualObserverStatus(
      bucket,
      availableAtEntry,
      observer.failureReason,
    );

    const rawLabel = options.label?.trim() ?? '';
    const submissionClass = options.submissionClass ?? null;
    const label = rawLabel.length > 0 ? rawLabel : null;
    const attributed = submissionClass !== null && label !== null;
    const fixedStepId = options.fixedStepId == null
      ? null
      : Math.max(0, Math.floor(options.fixedStepId));
    const fixedStepSpan = Math.max(1, Math.floor(options.fixedStepSpan ?? 1));
    const substepIndex = options.substepIndex == null
      ? null
      : Math.max(0, Math.floor(options.substepIndex));
    const onSubmit = (observation: GPUQueueSubmitObservation): void => {
      this.appendActualObservation(queue, bucket, observation, {
        frameId,
        submissionClass,
        label,
        attributed,
        fixedStepId,
        fixedStepSpan,
        substepIndex,
      });
    };

    try {
      return observer.withScope(onSubmit, callback);
    } finally {
      if (availableAtEntry) {
        const exitCursor = observer.cursor;
        const history = observer.readHistory(entryCursor, exitCursor);
        this.pruneActualClaims(queue, history.retainedStartCursor);
        if (!history.complete) {
          this.updateActualObserverStatus(
            bucket,
            false,
            history.failureReason ?? 'GPUQueue submit history is incomplete for this scope.',
          );
        } else {
          for (const observation of history.observations) {
            if (this.hasActualClaim(queue, observation.sequence)) continue;
            // A deeper observer scope owns this submit, possibly through a
            // different ledger sharing the queue. Entries at this scope depth
            // should already have reached onSubmit; if they did not, preserve
            // the real submit as unattributed instead of silently losing it.
            if (observation.scopeDepth > scopeDepth) continue;
            this.appendActualObservation(queue, bucket, observation, {
              frameId,
              submissionClass: null,
              label: null,
              attributed: false,
              fixedStepId,
              fixedStepSpan,
              substepIndex,
            });
          }
        }
      }
      if (observer.listenerFailureCount !== listenerFailuresAtEntry) {
        this.updateActualObserverStatus(
          bucket,
          false,
          'GPUQueue.submit observation listener failed while this scope was active.',
        );
      }
      // Detect a callback that replaced the hook after the entry check. Such a
      // frame is partial and must never report an apparently valid match.
      this.updateActualObserverStatus(
        bucket,
        observer.available,
        observer.failureReason,
      );
    }
  }

  snapshot(frameId = this.currentFrameId): PhysicsSubmissionSnapshot {
    const resolvedFrame = Math.max(0, Math.floor(frameId));
    const bucket = this.frames.get(resolvedFrame) ?? emptyFrameBucket();
    const counts = emptyCounts();
    for (const record of bucket.records) {
      counts[record.submissionClass] += record.count;
    }
    // One broadphase submit plus four default collision substeps is the steady
    // production target. Catch-up frames remain visible as over-budget rather
    // than being averaged away.
    const steadyCriticalBudget = 5;
    const coreSubmitIntents = counts.critical + counts.topology + counts.init;
    const diagnosticReadbackIntents = counts.diagnostic;
    const totalOwnedSubmitIntents = coreSubmitIntents + diagnosticReadbackIntents;
    const actualCounts = emptyActualCounts();
    for (const record of bucket.actualRecords) {
      actualCounts.submitCalls++;
      if (record.commandBufferCount === null) actualCounts.unknownCommandBufferCountSubmits++;
      else actualCounts.knownCommandBuffers += record.commandBufferCount;
      if (record.attributed) actualCounts.attributedSubmits++;
      else actualCounts.unattributedSubmits++;
      if (record.submissionClass !== null) actualCounts.byClass[record.submissionClass]++;
    }
    const actualSubmitObservationAvailable = bucket.actualObservationAttempted
      && bucket.actualObservationAvailable;
    const actualDelta = actualSubmitObservationAvailable
      ? actualCounts.submitCalls - totalOwnedSubmitIntents
      : null;
    const byClassDelta = {} as Record<PhysicsSubmissionClass, number | null>;
    for (const submissionClass of SUBMISSION_CLASSES) {
      byClassDelta[submissionClass] = actualSubmitObservationAvailable
        ? actualCounts.byClass[submissionClass] - counts[submissionClass]
        : null;
    }
    return {
      frameId: resolvedFrame,
      fixedSteps: bucket.fixedSteps,
      substepsPerFixedStep: bucket.substepsPerFixedStep,
      advancedSimulation: bucket.advancedSimulation,
      counts,
      coreSubmitIntents,
      diagnosticReadbackIntents,
      totalOwnedSubmitIntents,
      criticalSubmits: counts.critical,
      steadyCriticalBudget,
      withinSteadyCriticalBudget: counts.critical <= steadyCriticalBudget,
      records: bucket.records.map((entry) => ({ ...entry })),
      cumulative: { ...this.cumulativeCounts },
      peakCriticalSubmits: this.peakCritical,
      peakOwnedSubmitIntents: this.peakOwned,
      actualSubmitObservationAttempted: bucket.actualObservationAttempted,
      actualSubmitObservationAvailable,
      actualSubmitObservationFailureReason: bucket.actualObservationFailureReason,
      actualRecords: bucket.actualRecords.map((entry) => ({ ...entry })),
      actualCounts: {
        ...actualCounts,
        byClass: { ...actualCounts.byClass },
      },
      unattributedActualSubmits: actualCounts.unattributedSubmits,
      actualIntentMismatch: {
        measured: actualSubmitObservationAvailable,
        intentSubmits: totalOwnedSubmitIntents,
        actualSubmits: actualCounts.submitCalls,
        delta: actualDelta,
        matches: actualDelta === null ? null : actualDelta === 0,
        byClassDelta,
      },
    };
  }

  reset(): void {
    this.frames.clear();
    for (const submissionClass of SUBMISSION_CLASSES) {
      this.cumulativeCounts[submissionClass] = 0;
    }
    this.currentFrameId = -1;
    this.sequence = 0;
    this.actualSequence = 0;
    this.peakCritical = 0;
    this.peakOwned = 0;
    this.actualClaimsByQueue = new WeakMap<object, QueueActualClaimState>();
  }

  private ensureFrame(frameId: number): FrameBucket {
    const boundedFrame = Math.max(0, Math.floor(frameId));
    let bucket = this.frames.get(boundedFrame);
    if (!bucket) {
      bucket = emptyFrameBucket();
      this.frames.set(boundedFrame, bucket);
      this.pruneHistory();
    }
    return bucket;
  }

  private updateActualObserverStatus(
    bucket: FrameBucket,
    available: boolean,
    failureReason: string | null,
  ): void {
    if (!bucket.actualObservationAttempted) {
      bucket.actualObservationAttempted = true;
      bucket.actualObservationAvailable = available;
    } else {
      // A frame measured through several queue scopes is valid only if every
      // one was observable. Never recover a partial frame back to available.
      bucket.actualObservationAvailable = bucket.actualObservationAvailable && available;
    }
    if (!available && bucket.actualObservationFailureReason === null) {
      bucket.actualObservationFailureReason = failureReason
        ?? 'GPUQueue.submit observation is unavailable for this frame.';
    }
  }

  private claimState(queue: GPUQueueSubmitTarget): QueueActualClaimState {
    let state = this.actualClaimsByQueue.get(queue);
    if (!state) {
      state = { sequences: new Set<number>(), retainedStartCursor: 0 };
      this.actualClaimsByQueue.set(queue, state);
    }
    return state;
  }

  private hasActualClaim(queue: GPUQueueSubmitTarget, sequence: number): boolean {
    return this.actualClaimsByQueue.get(queue)?.sequences.has(sequence) ?? false;
  }

  private appendActualObservation(
    queue: GPUQueueSubmitTarget,
    bucket: FrameBucket,
    observation: GPUQueueSubmitObservation,
    context: Pick<
      PhysicsActualSubmissionRecord,
      | 'frameId'
      | 'submissionClass'
      | 'label'
      | 'attributed'
      | 'fixedStepId'
      | 'fixedStepSpan'
      | 'substepIndex'
    >,
  ): boolean {
    const claims = this.claimState(queue);
    if (claims.sequences.has(observation.sequence)) return false;
    claims.sequences.add(observation.sequence);
    bucket.actualRecords.push({
      sequence: this.actualSequence++,
      queueSequence: observation.sequence,
      commandBufferCount: observation.commandBufferCount,
      scopeDepth: observation.scopeDepth,
      ...context,
    });
    return true;
  }

  private pruneActualClaims(queue: GPUQueueSubmitTarget, retainedStartCursor: number): void {
    const claims = this.actualClaimsByQueue.get(queue);
    if (!claims || retainedStartCursor <= claims.retainedStartCursor) return;
    for (const sequence of claims.sequences) {
      if (sequence < retainedStartCursor) claims.sequences.delete(sequence);
    }
    claims.retainedStartCursor = retainedStartCursor;
  }

  private pruneHistory(): void {
    if (this.frames.size <= this.historyLimit) return;
    const frameIds = [...this.frames.keys()].sort((a, b) => a - b);
    const removeCount = frameIds.length - this.historyLimit;
    for (let i = 0; i < removeCount; i++) {
      this.frames.delete(frameIds[i]);
    }
  }
}

export type PhysicsSubmissionLedgerSelfTestResult = {
  ok: boolean;
  checks: number;
  failures: readonly string[];
};

/** GPU-free actual-submit accounting and ledger-isolation checks. */
export function runPhysicsSubmissionLedgerSelfTest(): PhysicsSubmissionLedgerSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };
  const observerSelfTest = runGPUQueueSubmitObserverSelfTest();
  checks += observerSelfTest.checks;
  for (const failure of observerSelfTest.failures) failures.push(`observer: ${failure}`);

  const queue: GPUQueueSubmitTarget = {
    submit(this: unknown, buffers: unknown[]): void {
      if (this !== queue) throw new TypeError('fake GPUQueue brand mismatch');
      void buffers;
    },
  };
  const ledgerA = new PhysicsSubmissionLedger();
  const ledgerB = new PhysicsSubmissionLedger();
  ledgerA.beginFrame(7);
  ledgerB.beginFrame(3);
  ledgerA.setFrameWorkload(7, 1, 1, true);
  ledgerA.withActualSubmitScope(queue, { frameId: 7 }, () => {
    queue.submit(['unattributed']);
    ledgerA.withActualSubmitScope(
      queue,
      {
        frameId: 7,
        submissionClass: 'critical',
        label: 'physics-step',
        fixedStepId: 11,
        substepIndex: 0,
      },
      () => queue.submit(['a', 'b']),
    );
    ledgerB.withActualSubmitScope(
      queue,
      { frameId: 3, submissionClass: 'diagnostic', label: 'ledger-b' },
      () => queue.submit(['b']),
    );
  });
  ledgerA.record('critical', 'physics-step', { frameId: 7, fixedStepId: 11, substepIndex: 0 });
  ledgerB.record('diagnostic', 'ledger-b', { frameId: 3 });
  const snapshotA = ledgerA.snapshot(7);
  const snapshotB = ledgerB.snapshot(3);
  check(
    'actual observer is attempted and available',
    snapshotA.actualSubmitObservationAttempted && snapshotA.actualSubmitObservationAvailable,
  );
  check('outer and attributed submits are counted', snapshotA.actualCounts.submitCalls === 2);
  check('nested ledger does not leak into outer ledger', snapshotA.actualRecords.length === 2);
  check('second ledger sees only its own nested scope', snapshotB.actualRecords.length === 1);
  check('unattributed submit is explicit', snapshotA.unattributedActualSubmits === 1);
  check(
    'missing inner scope is caught once by the outer scope',
    snapshotA.actualRecords.filter((entry) => !entry.attributed).length === 1
      && new Set(snapshotA.actualRecords.map((entry) => entry.queueSequence)).size
        === snapshotA.actualRecords.length,
  );
  check(
    'inner and outer scope reconciliation does not duplicate submits',
    snapshotA.actualRecords.filter((entry) => entry.label === 'physics-step').length === 1,
  );
  check(
    'attributed class and command buffers are retained',
    snapshotA.actualCounts.byClass.critical === 1
      && snapshotA.actualCounts.knownCommandBuffers === 3,
  );
  check(
    'actual-intent mismatch is measured',
    snapshotA.actualIntentMismatch.measured
      && snapshotA.actualIntentMismatch.delta === 1
      && snapshotA.actualIntentMismatch.matches === false,
  );
  check(
    'matching nested ledger is reported',
    snapshotB.actualIntentMismatch.matches === true
      && snapshotB.actualIntentMismatch.delta === 0,
  );

  const lockedQueue = Object.freeze({ submit(): void {} }) as GPUQueueSubmitTarget;
  const unavailableLedger = new PhysicsSubmissionLedger();
  unavailableLedger.beginFrame(1);
  let callbackRan = false;
  unavailableLedger.withActualSubmitScope(lockedQueue, { frameId: 1 }, () => {
    callbackRan = true;
    lockedQueue.submit([]);
  });
  const unavailableSnapshot = unavailableLedger.snapshot(1);
  check('unavailable hook still executes callback', callbackRan);
  check('unavailable hook is explicit', !unavailableSnapshot.actualSubmitObservationAvailable);
  check('unavailable mismatch is not fabricated', !unavailableSnapshot.actualIntentMismatch.measured);
  check('unavailable reason is retained', Boolean(unavailableSnapshot.actualSubmitObservationFailureReason));

  const gapQueue: GPUQueueSubmitTarget = { submit(): void {} };
  const gapObserver = getGPUQueueSubmitObserver(gapQueue);
  const gapLedger = new PhysicsSubmissionLedger();
  gapLedger.beginFrame(9);
  gapLedger.withActualSubmitScope(gapQueue, { frameId: 9 }, () => {
    // A deeper non-ledger scope owns these calls, so the outer listener does
    // not receive them directly. Overflowing the bounded history must make
    // the outer audit explicitly unavailable rather than fabricate a match.
    gapObserver.withScope(
      () => undefined,
      () => {
        for (let i = 0; i <= GPU_QUEUE_SUBMIT_HISTORY_CAPACITY; i++) {
          gapQueue.submit([]);
        }
      },
    );
  });
  const gapSnapshot = gapLedger.snapshot(9);
  check(
    'bounded history gap makes actual observation unavailable',
    gapSnapshot.actualSubmitObservationAttempted
      && !gapSnapshot.actualSubmitObservationAvailable
      && gapSnapshot.actualSubmitObservationFailureReason?.includes('history gap') === true,
  );
  check(
    'history gap cannot fabricate an actual-intent comparison',
    !gapSnapshot.actualIntentMismatch.measured
      && gapSnapshot.actualIntentMismatch.matches === null,
  );

  return { ok: failures.length === 0, checks, failures };
}
